import { useMemo } from 'react';
import { generatePath } from 'react-router';
import { CARD_GAME_ROUTE, CATCH_GAME_ROUTE, EVENT_SHOP_ROUTE } from 'src/config/event/eventConfig';
import { useGetLangFromUrl } from 'src/modules/hooks/useGetLangFromUrl';

export const LANG_PARAM = '/:lang?/';

export const useRoute = () => {
  const lang = useGetLangFromUrl();

  const ROUTE = useMemo(() => {
    return {
      LOGIN: {
        path: 'login',
        get: (email: string) => generatePath(LANG_PARAM + ROUTE.LOGIN.path, { lang, email })
      },
      SIGNUP: {
        path: 'signup',
        get: (email: string) => generatePath(LANG_PARAM + ROUTE.SIGNUP.path, { lang, email })
      },
      AUTH: {
        path: 'auth',
        get: () => generatePath(LANG_PARAM + ROUTE.AUTH.path, { lang })
      },
      AVIATOR: {
        path: 'game/34642',
        get: () => generatePath(LANG_PARAM + ROUTE.AVIATOR.path, { lang })
      },
      DRIVER: {
        path: 'game/15347',
        get: () => generatePath(LANG_PARAM + ROUTE.DRIVER.path, { lang })
      },
      MAIN: {
        path: '/',
        get: () => generatePath(LANG_PARAM + ROUTE.MAIN.path, { lang })
      },
      TOURNAMENTS: {
        path: 'tournaments',
        get: () => generatePath(LANG_PARAM + ROUTE.TOURNAMENTS.path, { lang })
      },
      NETWORK_TOURNAMENT: {
        path: 'tournaments/:tournamentCode',
        get: (tournamentCode: string) =>
          generatePath(LANG_PARAM + ROUTE.NETWORK_TOURNAMENT.path, { lang, tournamentCode })
      },
      TOURNAMENTS_LOCAL: {
        path: 'tournaments/local/:tournamentId',
        get: (tournamentId: string) =>
          generatePath(LANG_PARAM + ROUTE.TOURNAMENTS_LOCAL.path, { lang, tournamentId })
      },
      BONUSES: {
        path: 'bonuses',
        get: () => generatePath(LANG_PARAM + ROUTE.BONUSES.path, { lang })
      },
      BONUS: {
        path: 'bonuses/:bonus',
        get: (bonus: string) => generatePath(LANG_PARAM + ROUTE.BONUS.path, { lang, bonus })
      },
      WHEEL_OF_FORTUNE: {
        path: 'wheel-of-fortune',
        get: () => generatePath(LANG_PARAM + ROUTE.WHEEL_OF_FORTUNE.path, { lang })
      },
      ACTIVATE_BONUS: {
        path: 'activate-bonus/:bonusId',
        get: (bonusId: string) =>
          generatePath(LANG_PARAM + ROUTE.ACTIVATE_BONUS.path, { lang, bonusId })
      },
      INFO_PAGES: {
        path: 'info_pages/:page',
        get: (page: string) => generatePath(LANG_PARAM + ROUTE.INFO_PAGES.path, { lang, page })
      },
      FAQ: {
        path: 'faq',
        get: () => generatePath(LANG_PARAM + ROUTE.FAQ.path, { lang })
      },
      GAME: {
        path: 'game/:gameId?/:demo?',
        get: (gameId: string, demo: string) =>
          generatePath(LANG_PARAM + ROUTE.GAME.path, { lang, gameId, demo })
      },
      GAMES: {
        path: 'games/:categoryId?/:subCategory?',
        get: (categoryId?: string, subCategory?: string) =>
          generatePath(LANG_PARAM + ROUTE.GAMES.path, { lang, categoryId, subCategory })
      },
      GAMES_PROVIDERS: {
        path: 'games?',
        get: () => generatePath(LANG_PARAM + ROUTE.GAMES_PROVIDERS.path, { lang })
      },
      GAMES_LIVE: {
        path: 'games/live',
        get: () => generatePath(LANG_PARAM + ROUTE.GAMES_LIVE.path, { lang })
      },
      VIP: {
        path: 'vip',
        get: () => generatePath(LANG_PARAM + ROUTE.VIP.path, { lang })
      },
      SPORT: {
        path: 'sport',
        get: () => generatePath(LANG_PARAM + ROUTE.SPORT.path, { lang })
      },
      PWA_INSTRUCTION: {
        path: 'install-app/:deviceId?',
        get: (deviceId: string) =>
          generatePath(LANG_PARAM + ROUTE.PWA_INSTRUCTION.path, { lang, deviceId })
      },
      GPT_LUCKY_PIGEON: {
        path: 'lucky-pigeon',
        get: () => generatePath(LANG_PARAM + ROUTE.GPT_LUCKY_PIGEON.path, { lang })
      },
      JACKPOTS: {
        path: 'jackpots',
        get: () => generatePath(LANG_PARAM + ROUTE.JACKPOTS.path, { lang })
      },
      ACCOUNT: {
        path: 'account',
        get: () => generatePath(LANG_PARAM + ROUTE.ACCOUNT.path, { lang })
      },
      ACCOUNT_PROFILE_MAIN: {
        path: 'data',
        get: () =>
          generatePath(
            LANG_PARAM + ROUTE.ACCOUNT.path + '/profile/' + ROUTE.ACCOUNT_PROFILE_MAIN.path,
            {
              lang
            }
          )
      },
      ACCOUNT_PROFILE: {
        path: 'profile/*',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_PROFILE.path, { lang })
      },
      ACCOUNT_BONUSES: {
        path: 'bonuses',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_BONUSES.path, { lang })
      },
      ACCOUNT_WITHDRAWAL: {
        path: 'withdrawal/*',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_WITHDRAWAL.path, {
            lang
          })
      },
      ACCOUNT_BONUS: {
        path: 'bonus/*',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_BONUS.path, { lang })
      },
      ACCOUNT_DEPOSIT: {
        path: 'deposit/*',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_DEPOSIT.path, {
            lang
          })
      },
      ACCOUNT_DEPOSIT_HISTORY: {
        path: 'deposit/history',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_DEPOSIT_HISTORY.path, {
            lang
          })
      },
      ACCOUNT_WALLET_HISTORY: {
        path: 'withdrawal/history',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_WALLET_HISTORY.path, {
            lang
          })
      },
      ACCOUNT_PROFILE_TOURNAMENTS: {
        path: 'tournaments/*',
        get: () =>
          generatePath(
            LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_PROFILE_TOURNAMENTS.path,
            {
              lang
            }
          )
      },
      ACCOUNT_USER_BALANCE: {
        path: 'userBalance/*',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_USER_BALANCE.path, {
            lang
          })
      },
      ACCOUNT_JACKPOTS: {
        path: 'jackpots/*',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_JACKPOTS.path, {
            lang
          })
      },
      ACCOUNT_PROMO: {
        path: 'promo',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_PROMO.path, {
            lang
          })
      },
      ACCOUNT_VIP: {
        path: 'vip',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_VIP.path, {
            lang
          })
      },
      DEPOSIT_STANDALONE: {
        path: 'deposit-standalone',
        get: () => generatePath(LANG_PARAM + ROUTE.DEPOSIT_STANDALONE.path, { lang })
      },
      DEPOSIT_LINK: {
        path: 'deposit',
        get: () => generatePath(LANG_PARAM + ROUTE.DEPOSIT_LINK.path, { lang })
      },
      DEPOSIT_SUCCESS: {
        path: 'deposit-success/:merchantId?',
        get: (merchantId: string) =>
          generatePath(LANG_PARAM + ROUTE.DEPOSIT_SUCCESS.path, { lang, merchantId })
      },
      DEPOSIT_FAILURE: {
        path: 'deposit-failure/*',
        get: () => generatePath(LANG_PARAM + ROUTE.DEPOSIT_FAILURE.path, { lang })
      },
      DEPOSIT_CANCEL: {
        path: 'deposit-cancel/*',
        get: () => generatePath(LANG_PARAM + ROUTE.DEPOSIT_CANCEL.path, { lang })
      },
      DEPOSIT_RETURN: {
        path: 'deposit-return/:merchantId?',
        get: (merchantId: string) =>
          generatePath(LANG_PARAM + ROUTE.DEPOSIT_RETURN.path, { lang, merchantId })
      },
      LANDING_WELCOME_BONUS: {
        path: 'landing/welcome-bonus',
        get: () => generatePath(LANG_PARAM + ROUTE.LANDING_WELCOME_BONUS.path, { lang })
      },
      LANDING_WELCOME_BONUS_VIP: {
        path: 'landing/welcome-bonus-vip',
        get: () => generatePath(LANG_PARAM + ROUTE.LANDING_WELCOME_BONUS_VIP.path, { lang })
      },
      REGISTER_REFERRAL: {
        path: 'register-referral/:referral',
        get: (referral: string) =>
          generatePath(LANG_PARAM + ROUTE.REGISTER_REFERRAL.path, { lang, referral })
      },
      REDIRECT_LOBBY: {
        path: 'redirect-lobby',
        get: () => generatePath(LANG_PARAM + ROUTE.REDIRECT_LOBBY.path, { lang })
      },
      WELCOME_BONUS: {
        path: 'welcome-landing',
        get: () => generatePath(LANG_PARAM + ROUTE.WELCOME_BONUS.path, { lang })
      },
      INFO_PAGES_CODE: {
        path: 'info_pages/:code',
        get: (code: string) => generatePath(LANG_PARAM + ROUTE.INFO_PAGES_CODE.path, { lang, code })
      },
      /** @deprecated */
      INFO_PAGES_POLICY: {
        path: 'info_pages/privacy_policy',
        get: () => generatePath(LANG_PARAM + ROUTE.INFO_PAGES_POLICY.path, { lang })
      },
      /** @deprecated */
      INFO_PAGES_TERMS: {
        path: 'info_pages/terms_and_conditions',
        get: () => generatePath(LANG_PARAM + ROUTE.INFO_PAGES_TERMS.path, { lang })
      },
      ACTION: {
        path: 'action',
        get: () => generatePath(LANG_PARAM + ROUTE.ACTION.path, { lang })
      },
      BLOCKED_COUNTRY: {
        path: 'blocked-country',
        get: () => generatePath(LANG_PARAM + ROUTE.BLOCKED_COUNTRY.path, { lang })
      },
      CATCH_GAME: {
        path: CATCH_GAME_ROUTE,
        get: () => generatePath(LANG_PARAM + ROUTE.CATCH_GAME.path, { lang })
      },
      CARD_GAME: {
        path: CARD_GAME_ROUTE,
        get: () => generatePath(LANG_PARAM + ROUTE.CARD_GAME.path, { lang })
      },
      EVENT_SHOP: {
        path: EVENT_SHOP_ROUTE,
        get: () => generatePath(LANG_PARAM + ROUTE.EVENT_SHOP.path, { lang })
      },
      REGISTRATION: {
        path: 'registration',
        get: () => generatePath(LANG_PARAM + ROUTE.REGISTRATION.path, { lang })
      },
      404: {
        path: '404',
        get: () => generatePath(LANG_PARAM + ROUTE[404].path, { lang })
      },
      ALL_ROUTES: {
        path: '*',
        get: () => generatePath(LANG_PARAM + ROUTE.ALL_ROUTES.path, { lang })
      },
      CUSTOM: {
        get: (path: string) => {
          return lang === 'en'
            ? path
            : generatePath(LANG_PARAM + path, {
                lang
              });
        }
      }
    };
  }, [lang]);

  const getPath = (path: string) => generatePath(LANG_PARAM + path, { lang });

  return { ROUTE, getPath };
};

export type RoutesType = keyof ReturnType<typeof useRoute>['ROUTE'];
